import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import AgentsAppBar, { AGENTS_APP_BAR_OFFSET_PX } from "../components/Agents/AgentsAppBar";
import AgentUsageMeter from "../components/Agents/AgentUsageMeter";
import AgentAiCreditMeter from "../components/Agents/AgentAiCreditMeter";
import { fetchAiUsage, getAgentUsage } from "../features/agents/agentApi";

const AgentUsagePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await getAgentUsage();
        const aiResult = await fetchAiUsage().catch(() => null);
        if (!cancelled) {
          setUsage(result);
          setAiUsage(aiResult);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = Array.isArray(usage?.by_agent) ? usage.by_agent : [];
  const lifetime = usage?.lifetime === true;
  const agentTotal = rows.reduce((sum, row) => sum + (Number(row.live_total) || 0), 0);
  const total = lifetime ? agentTotal : Number(usage?.live_total ?? 0) || 0;
  const workflowUsage = Number(usage?.creditUsage) || 0;

  return (
    <>
      <AgentsAppBar
        title="Agent usage"
        backTooltip="Back to agents"
        onBack={() => history.push("/agents")}
      />
      <Box
        style={{
          padding: isMobile
            ? `${AGENTS_APP_BAR_OFFSET_PX}px 12px 16px 12px`
            : `${AGENTS_APP_BAR_OFFSET_PX}px 24px 24px 24px`,
          minHeight: "100vh",
          width: "100%",
          boxSizing: "border-box",
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box maxWidth={900} mx="auto">
          <Typography variant="body2" color="textSecondary" paragraph>
            {lifetime
              ? "Free plan includes 300 task credits for the life of the account. They do not reset each month. Upgrade to a paid plan once they are used. The meter is total task credits. The table is live agent tasks."
              : "Live agent tasks for this account in the current billing period. Each tool call on a live run counts as one task. Preview and editor test runs are excluded."}
          </Typography>
          {loading ? (
            <Box display="flex" justifyContent="center" mt={6}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <AgentUsageMeter usage={usage} />
              {aiUsage ? <AgentAiCreditMeter usage={aiUsage} /> : null}
              <Alert severity="info" variant="outlined" style={{ marginBottom: 16 }}>
                {lifetime ? (
                  <>
                    This page breaks down <strong>live agent tasks</strong> (tool calls) for the life of
                    the free plan. Preview and editor test runs are excluded.
                  </>
                ) : (
                  <>
                    This page breaks down <strong>live tasks</strong> (tool calls) in the current billing
                    period — preview and editor test runs are excluded.
                  </>
                )}
                {workflowUsage > 0 ? (
                  <> Current tenant creditUsage: {workflowUsage.toLocaleString()}.</>
                ) : null}
              </Alert>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Agent</TableCell>
                      <TableCell align="right">Live tasks</TableCell>
                      <TableCell align="right">Share</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" color="textSecondary">
                            {lifetime
                              ? "No live agent tasks yet."
                              : "No live agent tasks in this billing period."}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => {
                        const n = Number(row.live_total) || 0;
                        const share = total > 0 ? Math.round((n / total) * 100) : 0;
                        return (
                          <TableRow
                            key={row.agent_id}
                            hover
                            style={{ cursor: "pointer" }}
                            onClick={() => history.push(`/agents/${row.agent_id}/tasks`)}
                          >
                            <TableCell>{row.name || row.agent_id}</TableCell>
                            <TableCell align="right">{n.toLocaleString()}</TableCell>
                            <TableCell align="right">{share}%</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default AgentUsagePage;
